/**
 * Shared "Profile" view consumed by Drive / Calendar / Contacts / Tasks
 * analysis pages. Each module passes its own section labels, API base,
 * and an optional coverage panel; the parsing, fetching, and rendering
 * are identical and live here.
 *
 * Backend contract per module:
 *   POST  ${apiBase}/analysis             — generate, returns { report, ... }
 *   GET   ${apiBase}/analysis-reports     — list past reports (newest first)
 *
 * The latest report's text is split into UPPERCASE-labeled sections using
 * the `sectionLabels` array. Within each section, lines starting with "- "
 * collapse into <ul>; lines ending in ":" (when `parseSubheadings` is on)
 * become <h3> dividers between bullet groups.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { http } from '@/lib/http';
import { getAuthItem } from '@/lib/authStorage';

interface AnalysisReport {
  id: string;
  reportNumber: number;
  text: string;
  messageCount: number | null;
  model: string | null;
  comment: string | null;
  createdAt: string;
}

export interface AnalysisProfileViewProps {
  /** Page title in the small uppercase header, e.g. "Drive Profile". */
  title: string;
  /** API base path — e.g. "/integrations/google/drive" or "/contacts". */
  apiBase: string;
  /** Section labels exactly as Claude is asked to emit them. Used to split the report. */
  sectionLabels: readonly string[];
  /** Suffix on the subhead count, e.g. "files analyzed" → "152 files analyzed". */
  analyzedCountSuffix: string;
  /** Body of the empty-state card shown before any report exists. */
  placeholderCopy: string;
  /** When true, single-line "Subheading:" lines inside a section render as h3 dividers. */
  parseSubheadings?: boolean;
  /** Optional stats panel rendered below the narrative. */
  coverage?: ReactNode;
}

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'subheading'; text: string };

interface Section { label: string; blocks: Block[]; }

function buildLabelMatcher(labels: readonly string[]) {
  const upperLabels = labels.map((l) => l.toUpperCase());
  return (raw: string): string | null => {
    const cleaned = raw
      .trim()
      .replace(/^[-*•#]+\s*/, '')
      .replace(/[*]/g, '')
      .replace(/:$/, '')
      .toUpperCase()
      .replace(/[’']/g, "'")
      .replace(/&AMP;/g, '&');
    const idx = upperLabels.indexOf(cleaned);
    return idx >= 0 ? labels[idx] : null;
  };
}

function isSubheadingLine(line: string): boolean {
  if (!line.endsWith(':')) return false;
  if (line.length > 80) return false;
  if (/^[-*•]/.test(line)) return false;
  return true;
}

function parseProfile(
  text: string,
  matchLabel: (raw: string) => string | null,
  parseSubheadings: boolean,
): Section[] {
  const lines = text.split('\n');
  const sections: Section[] = [];
  let current: Section | null = null;
  let paraBuffer: string[] = [];
  let listBuffer: string[] | null = null;

  const flushPara = () => {
    if (!current) { paraBuffer = []; return; }
    const t = paraBuffer.join(' ').trim();
    if (t) current.blocks.push({ kind: 'p', text: t });
    paraBuffer = [];
  };
  const flushList = () => {
    if (!current) { listBuffer = null; return; }
    if (listBuffer && listBuffer.length) {
      current.blocks.push({ kind: 'list', items: [...listBuffer] });
    }
    listBuffer = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    const isBullet = /^[-*•]\s+/.test(line);

    const colonMatch = line.match(/^([A-Z'’&\s]+):\s*(.*)$/);
    if (colonMatch) {
      const maybe = matchLabel(colonMatch[1]);
      if (maybe) {
        flushPara(); flushList();
        current = { label: maybe, blocks: [] };
        sections.push(current);
        if (colonMatch[2]) paraBuffer.push(colonMatch[2]);
        continue;
      }
    }
    const labelOnly = matchLabel(line);
    if (labelOnly) {
      flushPara(); flushList();
      current = { label: labelOnly, blocks: [] };
      sections.push(current);
      continue;
    }
    if (!current) continue;
    if (line === '' || /^-{3,}$/.test(line)) {
      flushPara(); flushList();
      continue;
    }
    if (parseSubheadings && !isBullet && isSubheadingLine(line)) {
      flushPara(); flushList();
      current.blocks.push({ kind: 'subheading', text: line.replace(/:$/, '') });
      continue;
    }
    if (isBullet) {
      flushPara();
      if (!listBuffer) listBuffer = [];
      listBuffer.push(line.replace(/^[-*•]\s+/, ''));
    } else {
      flushList();
      paraBuffer.push(line);
    }
  }
  flushPara(); flushList();
  return sections;
}

function SectionBlock({ section, first }: { section: Section; first: boolean }) {
  return (
    <section style={{
      marginTop: first ? 36 : 44,
      paddingTop: first ? 0 : 24,
      borderTop: first ? 'none' : '1px solid #f3f4f6',
    }}>
      <h2 style={{
        fontSize: 13, fontWeight: 700, letterSpacing: '0.14em',
        color: 'var(--color-text-muted)', textTransform: 'uppercase',
        margin: '0 0 14px 0',
      }}>{section.label}</h2>
      {section.blocks.map((b, i) => {
        if (b.kind === 'p') {
          return (
            <p key={i} style={{ margin: '0 0 12px 0', fontSize: 15, lineHeight: 1.65, color: '#111827' }}>
              {b.text}
            </p>
          );
        }
        if (b.kind === 'subheading') {
          return (
            <h3 key={i} style={{
              margin: '18px 0 8px 0', fontSize: 14, fontWeight: 600,
              color: '#111827', letterSpacing: '0.02em',
            }}>
              {b.text}
            </h3>
          );
        }
        return (
          <ul key={i} style={{ margin: '0 0 14px 0', paddingLeft: 20 }}>
            {b.items.map((item, j) => (
              <li key={j} style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 6, color: '#111827' }}>
                {item}
              </li>
            ))}
          </ul>
        );
      })}
    </section>
  );
}

function fmtRefreshed(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `today · ${time}`;
  if (isYesterday) return `yesterday · ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
}

export function AnalysisProfileView({
  title,
  apiBase,
  sectionLabels,
  analyzedCountSuffix,
  placeholderCopy,
  parseSubheadings = false,
  coverage,
}: AnalysisProfileViewProps) {
  const [reports, setReports] = useState<AnalysisReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matchLabel = useMemo(() => buildLabelMatcher(sectionLabels), [sectionLabels]);

  // Pass the Bearer token explicitly — http only attaches Authorization when
  // authToken is provided. Some analysis routes (e.g. calendar) sit behind
  // strict jwtAuthMiddleware that rejects the X-User-Id header fallback.
  const authToken = getAuthItem('xavior_auth_token') ?? undefined;

  const loadReports = useCallback(async () => {
    try {
      const { data } = await http.get<{ reports?: AnalysisReport[] }>(`${apiBase}/analysis-reports`, { authToken });
      setReports(Array.isArray(data?.reports) ? data.reports : []);
    } catch { /* silent — empty list is a fine default */ }
  }, [apiBase, authToken]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const generate = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // http throws an Error with the server's message on non-2xx, so the
      // catch below surfaces it directly.
      await http.post(`${apiBase}/analysis`, {}, { authToken });
      await loadReports();
    } catch (e: any) {
      const msg = typeof e?.message === 'string' && e.message
        ? e.message
        : 'Failed to generate analysis';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [apiBase, loadReports, authToken]);

  const latest = reports[0];
  const sections = useMemo(
    () => latest ? parseProfile(latest.text, matchLabel, parseSubheadings) : [],
    [latest, matchLabel, parseSubheadings],
  );

  return (
    <div className="xv-scroll" style={{
      position: 'absolute', inset: 0, background: 'var(--color-surface, #fff)',
      overflow: 'auto', padding: '28px 36px 60px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{
              fontSize: 17, fontWeight: 700, letterSpacing: '0.14em',
              color: '#2563eb', textTransform: 'uppercase', marginBottom: 8,
            }}>{title}</div>
            {latest ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
                Last refreshed {fmtRefreshed(latest.createdAt)}
                {latest.messageCount != null && ` · ${latest.messageCount} ${analyzedCountSuffix}`}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No profile yet</div>
            )}
          </div>
          <button type="button" onClick={generate} disabled={loading} style={{
            background: 'var(--color-surface)', color: loading ? '#9ca3af' : '#374151',
            border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 14px', fontSize: 13,
            fontWeight: 500, cursor: loading ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <ArrowPathIcon width={15} height={15} />
            {loading ? 'Analyzing…' : (latest ? 'Refresh' : 'Generate')}
          </button>
        </div>

        {error && (
          <div style={{
            marginTop: 16, padding: 12, background: '#fef2f2', color: '#991b1b',
            borderRadius: 6, fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {sections.length > 0 ? (
          <div>
            {sections.map((s, i) => <SectionBlock key={`${s.label}-${i}`} section={s} first={i === 0} />)}
          </div>
        ) : latest ? (
          <div style={{ marginTop: 32 }}>
            {latest.text.split('\n\n').map((p, i) => (
              <p key={i} style={{ margin: '0 0 12px 0', fontSize: 15, lineHeight: 1.65, color: '#111827' }}>
                {p.trim()}
              </p>
            ))}
          </div>
        ) : !loading ? (
          <div style={{
            marginTop: 40, padding: 24, border: '1px dashed #d1d5db', borderRadius: 8,
            color: 'var(--color-text-faint)', fontSize: 14, lineHeight: 1.55,
          }}>
            {placeholderCopy}
          </div>
        ) : null}

        {coverage && (
          <div style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid #f3f4f6' }}>
            {coverage}
          </div>
        )}
      </div>
    </div>
  );
}
